import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import backend from '../../services/backend';
import type { ProcessingDataClassification } from '@avisoma/shared';
import { confidenceBandText } from '../../lib/confidenceBand';

export type PreviewablePage = { id: string; classification?: ProcessingDataClassification };

export type PreviewAnchor = { left: number; top: number; right: number; bottom: number };

const W = 288;
const GAP = 12;

// Score colours come from the shared band helper so the hover popover and the
// full detail table read the same green / amber / red language.
const scoreColor = confidenceBandText;

// Floating preview shown while hovering a page card: the page's classified title
// and description (fetched on demand) plus its top-5 classification scores (already
// on the card). Position anchors to the card, flips left / clamps so it never
// spills off-screen. pointer-events-none so it never steals the hover.
export function PagePreviewPopover({ page, anchor }: { page: PreviewablePage; anchor: PreviewAnchor }) {
  const { data, isLoading } = useQuery({
    queryKey: ['documentPage', page.id],
    queryFn: () => backend.getDocumentPage(page.id).then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });

  const cd = data?.processing?.pdfParser?.result?.classificationData;
  // Prefer the top-5 the caller already had inline (table shows instantly); else
  // fall back to the freshly-fetched page's classification, so call sites that only
  // know the id (similar pages, reconstruction, curation, parent page, …) still
  // render the classification table.
  const top5 = (page.classification ?? data?.processing?.classification?.result ?? []).slice(0, 5);

  const flipLeft = anchor.right + GAP + W > window.innerWidth;
  const left = flipLeft ? Math.max(8, anchor.left - GAP - W) : anchor.right + GAP;
  const top = Math.max(8, Math.min(anchor.top, window.innerHeight - 360));

  return createPortal(
    <div
      className="pointer-events-none fixed z-50 rounded-lg border border-gray-200 bg-white p-3 shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
      style={{ left, top, width: W }}
    >
      {cd?.title && <div className="text-xs font-semibold text-gray-900 dark:text-zinc-100">{cd.title}</div>}
      {cd?.description && (
        <p className="mt-0.5 line-clamp-5 text-[11px] leading-snug text-gray-500 dark:text-zinc-400">{cd.description}</p>
      )}
      {isLoading && !cd && (
        <div className="space-y-1.5">
          <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-zinc-700" />
          <div className="h-2.5 w-full animate-pulse rounded bg-gray-200 dark:bg-zinc-700" />
        </div>
      )}
      {!isLoading && !cd?.title && !cd?.description && (
        <div className="text-[11px] italic text-gray-400 dark:text-zinc-500">No title or description</div>
      )}

      {top5.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2 dark:border-zinc-700">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">
            Top classifications
          </div>
          <table className="w-full table-fixed text-[11px]">
            <tbody>
              {top5.map(([category, score], i) => (
                <tr key={i}>
                  <th className="truncate py-0.5 pr-2 text-left font-normal text-gray-600 dark:text-zinc-400" title={category}>
                    {category}
                  </th>
                  <td className={clsx('w-10 py-0.5 text-right tabular-nums', scoreColor(score))}>
                    {(score * 100).toFixed(0)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>,
    document.body,
  );
}
